import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Stack, Typography, Paper, TextField, Checkbox, FormControlLabel,
  Button, Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Chip, Box, InputAdornment, CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, MenuItem, Alert, Divider, Tooltip,
} from "@mui/material";
import SyncIcon from "@mui/icons-material/SyncOutlined";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import ReplayIcon from "@mui/icons-material/ReplayOutlined";
import CheckIcon from "@mui/icons-material/CheckOutlined";
import { apiGet, apiPost, apiPatch, ApiError } from "../api/client";
import ImageGalleryCell, { parseImageUrls } from "../components/ImageGalleryCell";
import { useToast } from "../ui/Toaster";

const FIELD_LABEL = {
  cleaned_title: "Title",
  value: "Price",
  image_urls: "Images",
  location: "Location",
  condition: "Condition",
  brand: "Brand",
  description: "Description",
  expiration_date: "Exp date",
  item_missing: "Inventory item missing",
};

// eBay's policy text demands literal mm/dd/yyyy. Mirror the server-side
// regex so the dialog disables Save until the user types a valid value.
const EXP_DATE_RE = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;

// Conditions accepted by the backend's PATCH /api/listings (and the
// retry endpoint forwards whatever the user picks straight to the item).
const CONDITION_CHOICES = [
  "New", "Open Box", "Used", "For parts",
];

function MissingFieldChips({ fields }) {
  if (!fields || fields.length === 0) {
    return <Chip size="small" label="No blockers detected" color="success" variant="outlined" />;
  }
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ rowGap: 0.5 }}>
      {fields.map((f) => (
        <Chip
          key={f}
          size="small"
          label={FIELD_LABEL[f] || f}
          color="warning"
          variant="outlined"
        />
      ))}
    </Stack>
  );
}

// Coerce the API row into the shape the dialog edits. Image URLs round-trip
// as a newline-joined textarea so the user can paste/edit easily.
function rowToDraft(row) {
  return {
    cleaned_title: row.title || row.raw_title || "",
    value: row.value != null ? String(row.value) : "",
    image_urls: parseImageUrls(row.image_urls).join("\n"),
    location: row.location || "",
    condition: row.condition || "",
    brand: row.brand || "",
    description: row.description || "",
    expiration_date: row.expiration_date || "",
  };
}

function RetryDialog({ open, row, onClose, onResult }) {
  const { error: toastError, success: toastSuccess } = useToast();
  const [draft, setDraft] = useState(() => rowToDraft(row || {}));
  const [busy, setBusy] = useState(false);
  // Last-attempt error message returned by the retry — drives the inline
  // alert that lets the user iterate without leaving the dialog.
  const [lastError, setLastError] = useState(null);
  const [missing, setMissing] = useState(row?.missing_fields || []);

  // Reset draft + error state whenever the dialog is opened on a new row.
  useEffect(() => {
    if (!open) return;
    setDraft(rowToDraft(row || {}));
    setLastError(null);
    setMissing(row?.missing_fields || []);
  }, [open, row]);

  if (!row) return null;

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const submit = async () => {
    setBusy(true);
    setLastError(null);
    try {
      const payload = {};
      // Only send fields that actually differ from the row, plus anything
      // currently flagged as missing — we want to push fixes through even
      // if the user didn't open that text box yet (e.g. brand changed).
      const original = rowToDraft(row);
      for (const k of Object.keys(draft)) {
        if (draft[k] !== original[k]) payload[k] = draft[k];
      }
      // Always send price if it's currently invalid (server rejects ≤0.01)
      // even if the user didn't change it — covers the case where the user
      // typed it then changed their mind back.
      if ((row.missing_fields || []).includes("value") && draft.value !== "") {
        payload.value = draft.value;
      }

      const res = await apiPost(`/api/retry_listing/${encodeURIComponent(row.lot_id)}`, payload);
      if (res?.success) {
        if (res.already_sold) {
          toastSuccess(
            "Already sold — resolved",
            `${row.lot_id}: lot is in sale_lots, failed row marked resolved`,
          );
        } else {
          const merged = Number(res.merged_siblings || 0);
          const total = 1 + merged;
          toastSuccess(
            merged > 0 ? `Listed (${total} units, ${merged} merged)` : "Listed",
            row.lot_id,
          );
        }
        onResult?.({ ok: true, lot_id: row.lot_id, merged: res.merged_siblings });
        onClose?.();
      } else {
        setLastError(res?.error || "Listing failed");
        if (Array.isArray(res?.missing_fields)) setMissing(res.missing_fields);
        // Bubble the row-level update so the page reflects the new note
        // without a full refetch.
        onResult?.({ ok: false, lot_id: row.lot_id, error: res?.error, missing_fields: res?.missing_fields });
      }
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : String(e);
      setLastError(msg);
      toastError("Retry failed", msg);
    } finally {
      setBusy(false);
    }
  };

  const isMissing = (k) => (missing || []).includes(k);
  const labelFor = (k, label) => (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <span>{label}</span>
      {isMissing(k) && <Chip size="small" label="needed" color="warning" variant="outlined" />}
    </Stack>
  );

  // For formula items, refuse to submit until the date matches mm/dd/yyyy —
  // saves a round-trip to eBay just to get the same answer back.
  const expInvalid = !!row.is_formula && !EXP_DATE_RE.test((draft.expiration_date || "").trim());

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <ReportProblemOutlinedIcon color="warning" />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Retry listing — {row.lot_id}
              </Typography>
              {row.group_count > 1 && (
                <Chip
                  size="small"
                  label={`× ${row.group_count} units`}
                  color="info"
                />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {row.title || row.raw_title || "(no title)"}
              {row.group_count > 1 && ` — publishes one listing with qty ${row.group_count}`}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {row.is_formula && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.25 }}>
              Baby formula listing
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
              eBay requires an expiration date. We'll add <em>EXP mm/dd/yyyy</em> to
              the title and the policy disclaimer to the description automatically.
            </Typography>
          </Alert>
        )}
        {row.notes && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.25 }}>
              Last failure
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {row.notes}
            </Typography>
          </Alert>
        )}
        {lastError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.25 }}>
              Latest attempt
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {lastError}
            </Typography>
          </Alert>
        )}

        <Stack spacing={2}>
          <TextField
            label={labelFor("cleaned_title", "Title")}
            value={draft.cleaned_title}
            onChange={(e) => set("cleaned_title", e.target.value)}
            inputProps={{ maxLength: 80 }}
            helperText={`${(draft.cleaned_title || "").length}/80 — eBay caps the title`}
            fullWidth
            size="small"
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label={labelFor("value", "Price (USD)")}
              value={draft.value}
              onChange={(e) => set("value", e.target.value)}
              type="number"
              inputProps={{ step: "0.01", min: "0.02" }}
              size="small"
              sx={{ flex: 1 }}
              helperText="Must be greater than $0.01"
            />
            <TextField
              label={labelFor("condition", "Condition")}
              value={draft.condition}
              onChange={(e) => set("condition", e.target.value)}
              select
              size="small"
              sx={{ flex: 1 }}
            >
              <MenuItem value="">— unchanged —</MenuItem>
              {CONDITION_CHOICES.map((c) => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </TextField>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label={labelFor("brand", "Brand")}
              value={draft.brand}
              onChange={(e) => set("brand", e.target.value)}
              size="small"
              sx={{ flex: 1 }}
            />
            <TextField
              label={labelFor("location", "Location")}
              value={draft.location}
              onChange={(e) => set("location", e.target.value)}
              size="small"
              sx={{ flex: 1 }}
              helperText={row.lot_id?.includes("PAL") ? "Required for PAL items" : "Optional"}
            />
          </Stack>
          {row.is_formula && (
            <TextField
              label={labelFor("expiration_date", "Expiration date (mm/dd/yyyy)")}
              value={draft.expiration_date}
              onChange={(e) => set("expiration_date", e.target.value)}
              size="small"
              fullWidth
              required
              placeholder="05/15/2027"
              error={expInvalid && draft.expiration_date.length > 0}
              helperText={
                expInvalid && draft.expiration_date.length > 0
                  ? "Use mm/dd/yyyy (e.g. 05/15/2027)"
                  : "Required for baby formula. Will be stamped on the title and disclaimer."
              }
            />
          )}
          <TextField
            label={labelFor("image_urls", "Image URLs (one per line)")}
            value={draft.image_urls}
            onChange={(e) => set("image_urls", e.target.value)}
            multiline
            minRows={2}
            maxRows={6}
            size="small"
            fullWidth
            placeholder="https://…/image1.jpg&#10;https://…/image2.jpg"
            helperText="At least one image is required"
          />
          <TextField
            label={labelFor("description", "Description")}
            value={draft.description}
            onChange={(e) => set("description", e.target.value)}
            multiline
            minRows={2}
            maxRows={4}
            size="small"
            fullWidth
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={busy || expInvalid}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <ReplayIcon />}
        >
          {busy ? "Retrying…" : "Save & Retry"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function FailedListings() {
  const { error: toastError, success: toastSuccess } = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showResolved, setShowResolved] = useState(false);
  const [savingKeys, setSavingKeys] = useState(() => new Set());
  const [activeRow, setActiveRow] = useState(null);

  const fetchRows = useCallback(async (signal) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (showResolved) params.set("include_resolved", "true");
      const url = "/api/failed_listings" + (params.toString() ? `?${params.toString()}` : "");
      const js = await apiGet(url, { signal });
      const data = Array.isArray(js?.results) ? js.results : [];
      setRows(data);
    } catch (e) {
      if (e?.name === "AbortError") return;
      toastError("Couldn't load failed listings", e instanceof ApiError ? e.message : String(e));
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [toastError, showResolved]);

  useEffect(() => {
    const ctrl = new AbortController();
    fetchRows(ctrl.signal);
    return () => ctrl.abort();
  }, [fetchRows]);

  const setSaving = (key, on) => {
    setSavingKeys((prev) => {
      const next = new Set(prev);
      if (on) next.add(key); else next.delete(key);
      return next;
    });
  };

  const updateResolved = useCallback(async (row, nextResolved) => {
    if (!row?.id) return;
    const key = `mu_${row.id}`;
    setRows((cur) => cur.map((r) => (r.id === row.id ? { ...r, resolved: nextResolved } : r)));
    setSaving(key, true);
    try {
      // Mirrors ManualUpdates.js — the PATCH endpoint takes a row id (or lot_id),
      // we send lot_id since that's what the existing PATCH expects.
      await apiPatch(`/api/manual_updates/${encodeURIComponent(row.lot_id)}`, {
        lot_id: row.lot_id, resolved: Boolean(nextResolved),
      });
      toastSuccess(nextResolved ? "Marked resolved" : "Reopened", row.lot_id);
    } catch (e) {
      setRows((cur) => cur.map((r) => (r.id === row.id ? { ...r, resolved: !nextResolved } : r)));
      toastError("Update failed", e instanceof ApiError ? e.message : String(e));
    } finally {
      setSaving(key, false);
    }
  }, [toastError, toastSuccess]);

  const onRetryResult = useCallback(({ ok, lot_id, error, missing_fields }) => {
    if (ok) {
      // Drop the row optimistically — server marked it resolved.
      setRows((cur) => cur.filter((r) => r.lot_id !== lot_id));
    } else {
      // Reflect new error + missing fields without a full refetch.
      setRows((cur) => cur.map((r) => (
        r.lot_id === lot_id
          ? { ...r, notes: error || r.notes, missing_fields: missing_fields || r.missing_fields }
          : r
      )));
    }
  }, []);

  const visibleRows = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => (
      String(r.lot_id || "").toLowerCase().includes(s) ||
      String(r.title || "").toLowerCase().includes(s) ||
      String(r.raw_title || "").toLowerCase().includes(s) ||
      String(r.notes || "").toLowerCase().includes(s) ||
      String(r.type || "").toLowerCase().includes(s)
    ));
  }, [rows, q]);

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
        spacing={{ xs: 1.5, sm: 0 }}
        sx={{ mb: { xs: 2, md: 3.5 } }}
      >
        <Box>
          <Typography variant="h1" sx={{ mb: 0.5, fontSize: { xs: '1.85rem', md: '2.5rem' } }}>
            Failed Listings
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>
            {visibleRows.length.toLocaleString()} failure{visibleRows.length === 1 ? "" : "s"}
            {showResolved ? " (incl. resolved)" : ""}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            startIcon={loading ? <CircularProgress size={15} color="inherit" /> : <SyncIcon />}
            variant="contained"
            onClick={() => fetchRows()}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
          <TextField
            placeholder="Search lot, title, error, type…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showResolved}
                onChange={(e) => setShowResolved(e.target.checked)}
                size="small"
              />
            }
            label="Show resolved"
          />
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ overflow: 'hidden', position: 'relative' }}>
        <TableContainer>
          <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap' } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 60 }} />
                <TableCell sx={{ width: 110 }}>Resolved</TableCell>
                <TableCell sx={{ width: 130 }}>Lot</TableCell>
                <TableCell>Item</TableCell>
                <TableCell sx={{ width: 220 }}>Needs</TableCell>
                <TableCell>Last error</TableCell>
                <TableCell sx={{ width: 150 }} align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && visibleRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    <Stack direction="row" alignItems="center" justifyContent="center" gap={1}>
                      <CircularProgress size={14} />
                      <span>Loading…</span>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
              {!loading && visibleRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <Stack alignItems="center" gap={1} sx={{ color: 'text.secondary' }}>
                      <CheckIcon fontSize="small" />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>No failed listings</Typography>
                      <Typography variant="caption">
                        Listings that fail to publish will appear here with a retry button.
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
              {visibleRows.map((row) => {
                const key = `mu_${row.id}`;
                const saving = savingKeys.has(key);
                return (
                  <TableRow key={row.id} hover sx={{ opacity: row.resolved ? 0.55 : 1 }}>
                    <TableCell sx={{ width: 60 }}>
                      <ImageGalleryCell imageUrlsStr={row.image_urls} thumbnailSize={40} />
                    </TableCell>
                    <TableCell sx={{ width: 110 }}>
                      <Checkbox
                        checked={!!row.resolved}
                        disabled={saving}
                        onChange={(e) => updateResolved(row, e.target.checked)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ width: 130, fontFamily: 'monospace', fontSize: '0.78rem' }}>
                      {row.lot_id || "—"}
                      <Stack direction="row" spacing={0.5} sx={{ mt: 0.25 }} flexWrap="wrap">
                        {row.type && (
                          <Chip
                            size="small"
                            label={row.type === "ebay_listing_fail_inventory" ? "inventory" : "publish"}
                            variant="outlined"
                            color="warning"
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                        )}
                        {row.group_count > 1 && (
                          <Chip
                            size="small"
                            label={`× ${row.group_count}`}
                            color="info"
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'normal' }}>
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {row.title || row.raw_title || "—"}
                        </Typography>
                        {row.is_formula && (
                          <Chip
                            size="small"
                            label="formula"
                            color="info"
                            variant="outlined"
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                        )}
                      </Stack>
                      {row.condition && (
                        <Typography variant="caption" color="text.secondary">
                          {row.condition} · ${Number(row.value || 0).toFixed(2)}
                          {row.expiration_date ? ` · EXP ${row.expiration_date}` : ''}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ width: 220, whiteSpace: 'normal' }}>
                      {row.item_exists
                        ? <MissingFieldChips fields={row.missing_fields} />
                        : <Chip size="small" label="Inventory item missing" color="error" />}
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.78rem', whiteSpace: 'normal', maxWidth: 420 }}>
                      <Tooltip title={row.notes || ""} placement="top-start">
                        <Box sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          fontFamily: 'monospace',
                          fontSize: '0.72rem',
                        }}>
                          {row.notes || "—"}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ width: 150 }} align="right">
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<ReplayIcon fontSize="small" />}
                        disabled={!row.item_exists || row.resolved}
                        onClick={() => setActiveRow(row)}
                      >
                        {row.group_count > 1 ? `Retry × ${row.group_count}` : "Retry"}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {loading && visibleRows.length > 0 && (
          <Box sx={{
            position: 'absolute', top: 8, right: 12,
            display: 'inline-flex', alignItems: 'center', gap: 0.75,
            bgcolor: 'background.paper', border: 1, borderColor: 'divider',
            borderRadius: 1, px: 1, py: 0.25, fontSize: '0.7rem',
            color: 'text.secondary',
          }}>
            <CircularProgress size={12} /> Refreshing
          </Box>
        )}
      </Paper>

      <RetryDialog
        open={!!activeRow}
        row={activeRow}
        onClose={() => setActiveRow(null)}
        onResult={onRetryResult}
      />
    </Box>
  );
}
